'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { hasSession } from '@/lib/auth';
import { getFavorites, getListingsByIds, getSavedSearches, searchListings } from '@/lib/api';
import { getSearchVisit } from '@/lib/savedSearchVisits';
import {
  deriveSignal,
  favoriteNews,
  MAX_FAVORITES_PROBED,
  newMatchQueries,
  otherSearchesWithNews,
  pickedForYou,
  pickNewMatchRail,
  PERSONAL_RAIL_CARDS,
  PICKED_FETCH_SIZE,
  type FavoriteNote,
  type NewMatchResult,
  type PersonalSignal,
} from '@/lib/personalFeed';
import { mergeSeenPrices, readSeenPrices, writeSeenPrices } from '@/lib/favoriteSeen';
import { getRecentSearches, type RecentSearch } from '@/lib/recentSearches';
import { railIsWorthShowing } from '@/lib/homeFeed';
import { newsLine } from '@/lib/availability';
import { filtersToApiParams } from '@/lib/searchParams';
import { formatPrice, localTodayISO } from '@/lib/utils';
import ListingCard from '@/components/search/ListingCard';
import HomeRail, { HomeRailItem, RAIL_PHOTO_SIZES } from './HomeRail';
import { RailSkeleton } from './RailSkeleton';

/**
 * The buyer's own half of the feed.
 *
 * **Where it sits, and why not at the top.** The directive is that new matches
 * are the first thing a signed-in buyer sees. They are the first *personalized*
 * rail — but the whole block renders after the first server rail, because the
 * server cannot know who is asking (auth lives in localStorage + Firebase, both
 * client-only) and inserting a block **above the fold** after hydration is a
 * measured layout shift: the resume strip cost deployed CLS 0.000 → **0.0252**
 * doing exactly that, and moving it below the first rail took it back to 0
 * (W14 R132). LCP is unaffected either way — the LCP element is the h1, which is
 * above all of this and already painted.
 *
 * **The auth boundary.** `hasSession()` answers "has this browser ever signed
 * in" synchronously from localStorage, before Firebase loads, so the placeholder
 * can appear immediately for a returning buyer and never for a stranger. A
 * failed or 401 fetch renders nothing at all: the guest rails are still on the
 * page, which is a complete page, not an error. (W14 R133)
 */
/** How long a placeholder may stand before the guest feed is the answer. */
const WAIT_LIMIT_MS = 8_000;

export default function PersonalFeed() {
  const user = useAuthStore((s) => s.user);
  const isInitialized = useAuthStore((s) => s.isInitialized);

  /* eslint-disable react-hooks/set-state-in-effect -- localStorage is client-only;
     a post-hydration read is the only correct moment and it runs once. */
  const [sessionSeen, setSessionSeen] = useState(false);
  useEffect(() => {
    setSessionSeen(hasSession());
  }, []);

  /**
   * A placeholder must have an end. `hasSession()` only says this browser signed
   * in *once*: if Firebase never loads (blocked, offline, a revoked token) auth
   * never initializes and nothing would ever settle the skeleton — an endless
   * shimmer over nothing, which is the exact defect this project already shipped
   * once in `useListingPhotos`. After this long the buyer gets the guest feed,
   * which is a whole page.
   */
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGaveUp(true), WAIT_LIMIT_MS);
    return () => clearTimeout(t);
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const userId = user?.id;

  const searchesQuery = useQuery({
    queryKey: ['home-saved-searches', userId],
    queryFn: getSavedSearches,
    enabled: !!userId,
    staleTime: 60_000,
  });
  const searches = searchesQuery.data ?? [];

  const newMatchesQuery = useQuery({
    queryKey: ['home-new-matches', userId, searches.map((s) => s.id).join(',')],
    queryFn: async () => {
      const queries = newMatchQueries(
        searches,
        (id) => getSearchVisit(userId!, id),
        Date.now()
      );
      return Promise.all(
        queries.map(async (q): Promise<NewMatchResult | null> => {
          try {
            const r = await searchListings({ ...q.params, perPage: PERSONAL_RAIL_CARDS });
            return { ...q, total: r.total, listings: r.listings ?? [] };
          } catch {
            // One saved search failing must not take the rail down.
            return null;
          }
        })
      );
    },
    enabled: !!userId && searches.length > 0,
    staleTime: 60_000,
  });

  const results = newMatchesQuery.data ?? [];
  const newMatches = pickNewMatchRail(results);
  const alsoNew = otherSearchesWithNews(results, newMatches?.id);

  /* ---------------- saved homes, with news ---------------- */

  const favoritesQuery = useQuery({
    queryKey: ['home-favorites', userId],
    queryFn: getFavorites,
    enabled: !!userId,
    staleTime: 60_000,
  });
  const favoriteIds = (favoritesQuery.data ?? [])
    .slice(0, MAX_FAVORITES_PROBED)
    .map((f) => f.listing_id);

  const favoriteListingsQuery = useQuery({
    queryKey: ['home-favorite-listings', favoriteIds.join(',')],
    queryFn: () => getListingsByIds(favoriteIds),
    enabled: favoriteIds.length > 0,
    staleTime: 60_000,
  });
  const savedHomes = favoriteListingsQuery.data ?? [];

  // Read the previous observation ONCE per user, before anything overwrites it —
  // comparing against a record we just refreshed would report no news, ever.
  const [seen, setSeen] = useState<ReturnType<typeof readSeenPrices>>({});
  /* eslint-disable react-hooks/set-state-in-effect -- localStorage read, once per user. */
  useEffect(() => {
    if (userId) setSeen(readSeenPrices(userId));
  }, [userId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // …then record today's prices for next time. Deliberately after the compare,
  // and merge-not-replace so showing the news doesn't destroy the evidence.
  useEffect(() => {
    if (!userId || savedHomes.length === 0) return;
    writeSeenPrices(
      userId,
      mergeSeenPrices(
        readSeenPrices(userId),
        savedHomes.map((l) => ({ id: l.id, price: l.price }))
      )
    );
  }, [userId, savedHomes]);

  const news = favoriteNews(savedHomes, seen, localTodayISO());

  /* ---------------- picked for you ---------------- */

  /* eslint-disable react-hooks/set-state-in-effect -- localStorage is client-only. */
  const [recents, setRecents] = useState<RecentSearch[]>([]);
  useEffect(() => {
    setRecents(getRecentSearches());
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  const signal = deriveSignal(searches, recents);

  const pickedQuery = useQuery({
    queryKey: ['home-picked', signal?.href],
    queryFn: () =>
      searchListings(filtersToApiParams(signal!.filters, PICKED_FETCH_SIZE)).then(
        (r) => r.listings ?? []
      ),
    enabled: !!signal,
    staleTime: 60_000,
  });

  // Homes already on the page above this rail. Repeating them would make the
  // feed look shorter than it is.
  const alreadyShown = new Set([
    ...(newMatches?.listings ?? []).map((l) => l.id),
    ...savedHomes.map((l) => l.id),
  ]);
  const picked = pickedForYou(pickedQuery.data ?? [], alreadyShown);

  // Waiting on the session, the saved searches, or their counts — all three are
  // "the buyer's rails are coming", and all three show the same reserved box.
  const waiting =
    sessionSeen &&
    !gaveUp &&
    ((!isInitialized && !user) ||
      (!!userId && (searchesQuery.isPending || favoritesQuery.isPending)) ||
      (searches.length > 0 && newMatchesQuery.isPending) ||
      (favoriteIds.length > 0 && favoriteListingsQuery.isPending));

  if (waiting) {
    return (
      <PersonalSection>
        <RailSkeleton />
      </PersonalSection>
    );
  }

  // A "picked for you" rail of one or two homes reads as a broken fetch — unlike
  // the new-match rail, nothing on screen explains the number — so it holds to the
  // curated floor.
  const showPicked = signal && railIsWorthShowing(picked);

  // Signed out, nothing saved, no signal, or the fetch failed → the guest rails
  // stand on their own. Never an empty shell.
  if (!newMatches && news.length === 0 && !showPicked) return null;

  return (
    <PersonalSection firstName={user?.first_name}>
      {newMatches && (
        <div>
          <HomeRail
            title={`New in “${newMatches.name}”`}
            subtitle={newMatchWindowLabel(newMatches)}
            href={newMatches.href}
            seeAllLabel={
              newMatches.total > newMatches.listings.length
                ? `See all ${newMatches.total}`
                : 'See all'
            }
            count={newMatches.listings.length}
          >
            {newMatches.listings.map((l) => (
              <HomeRailItem key={l.id}>
                <ListingCard listing={l} photoSizes={RAIL_PHOTO_SIZES} viewTransitionEnabled={false} />
              </HomeRailItem>
            ))}
          </HomeRail>

          {alsoNew.length > 0 && (
            <p className="mx-auto max-w-7xl px-4 pb-6 text-sm text-ink-muted">
              {alsoNew.length === 1
                ? `“${alsoNew[0].name}” has ${alsoNew[0].total} new too.`
                : `${alsoNew.length} more saved searches have new matches.`}{' '}
              <Link
                href="/settings/saved-searches"
                className="font-medium text-emerald-700 underline-offset-4 hover:text-emerald-600 hover:underline"
              >
                All saved searches
              </Link>
            </p>
          )}
        </div>
      )}

      {news.length > 0 && (
        <HomeRail
          title="What changed on your saved homes"
          subtitle="Price cuts, open houses, and homes that have left the market"
          href="/favorites"
          seeAllLabel="All saved homes"
          count={news.length}
        >
          {news.map((n) => (
            <HomeRailItem key={n.listing.id}>
              <ListingCard
                listing={n.listing}
                photoSizes={RAIL_PHOTO_SIZES}
                hideFavorite
                viewTransitionEnabled={false}
              />
              <ul className="mt-2 space-y-1">
                {n.notes.map((note, i) => (
                  <li key={i} className="text-sm text-ink-muted">
                    {noteLabel(note)}
                  </li>
                ))}
              </ul>
            </HomeRailItem>
          ))}
        </HomeRail>
      )}

      {showPicked && (
        <HomeRail
          title="Picked for you"
          subtitle={signalLabel(signal)}
          href={signal.href}
          count={picked.length}
        >
          {picked.map((l) => (
            <HomeRailItem key={l.id}>
              <ListingCard listing={l} photoSizes={RAIL_PHOTO_SIZES} viewTransitionEnabled={false} />
            </HomeRailItem>
          ))}
        </HomeRail>
      )}
    </PersonalSection>
  );
}

/**
 * One line of real news about a saved home.
 *
 * Each is a number or a date we can point at: the dollars **and** the percentage
 * for a cut (a $38,000 reduction means something different on a $750k home than on
 * a $4M one), and the day we recorded the old price for an observed drop — because
 * that is the only thing we actually know (see `lib/favoriteSeen`).
 */
function noteLabel(note: FavoriteNote): string {
  const pct = (p: number) => `${Math.round(p * 100)}%`;
  switch (note.kind) {
    case 'drop':
      return `▼ ${formatPrice(note.amount)} (${pct(note.pct)}) since ${shortDate(note.sinceMs)}`;
    case 'below-original':
      return `${formatPrice(note.amount)} (${pct(note.pct)}) below its original ask`;
    case 'open-house':
      return `Open house ${dayLabel(note.date, note.thisWeekend)}, ${timeRange(note.start, note.end)}`;
    case 'unavailable':
      // The one note that is not a reason to act. Same words the detail banner
      // and the card badge use, plus the date our pipeline recorded — the only
      // thing the badge beside it doesn't already say. (`lib/availability`)
      return newsLine(note.availability, note.changed);
  }
}

function shortDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Open-house dates are date-only values — read them as UTC, never as instants.
 * A weekday alone ("Sat") is ambiguous three weeks out, so anything outside this
 * weekend carries its date.
 */
function dayLabel(day: string, thisWeekend: boolean): string {
  const d = new Date(`${day}T12:00:00Z`);
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  if (thisWeekend) return `this ${weekday}`;
  return `${weekday} ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

function timeRange(start: string, end: string): string {
  const parse = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return Number.isFinite(h) ? { h, m: m || 0 } : null;
  };
  const a = parse(start);
  const b = parse(end);
  if (!a || !b) return `${start}–${end}`;
  const clock = (t: { h: number; m: number }) =>
    `${t.h % 12 || 12}${t.m ? `:${String(t.m).padStart(2, '0')}` : ''}`;
  const meridiem = (t: { h: number; m: number }) => (t.h >= 12 ? 'pm' : 'am');
  // "1:30–4:30pm" rather than "1:30pm–4:30pm" when both sides share the half.
  return meridiem(a) === meridiem(b)
    ? `${clock(a)}–${clock(b)}${meridiem(b)}`
    : `${clock(a)}${meridiem(a)}–${clock(b)}${meridiem(b)}`;
}

/**
 * Why these homes. "Picked for you" is a claim, so the line under it says exactly
 * what the pick was made from — the saved search by name, or the place they
 * searched — and lists the filters in words. Nothing is inferred beyond what the
 * buyer typed.
 */
function signalLabel(signal: PersonalSignal): string {
  const terms = signal.terms.join(' · ');
  return signal.source === 'saved-search'
    ? `From your saved search “${signal.label}”${terms ? `: ${terms}` : ''}`
    : `Because you searched ${signal.label}${terms ? ` · ${terms}` : ''}`;
}

/** "3 new since you last looked · listed in the past 2 days" — never a guess. */
function newMatchWindowLabel(r: NewMatchResult): string {
  const since =
    r.baseline === 'visit' ? 'since you last opened this search' : 'since you saved this search';
  const window = r.days === 1 ? 'listed in the past day' : `listed in the past ${r.days} days`;
  return `${r.total.toLocaleString()} new ${since} · ${window}`;
}

/**
 * The buyer's section, marked as theirs. A tinted band with one eyebrow rather
 * than per-rail badges, so it is obvious at a glance which rails are about them
 * and which are the market.
 */
function PersonalSection({
  firstName,
  children,
}: {
  firstName?: string;
  children: React.ReactNode;
}) {
  return (
    /* No border of its own: `StableInsert` divides its children and the feed's
       own `divide-y` draws the line above it. Two rules setting the same edge
       rendered a 2 px hairline. (W14 R140) */
    <section data-personal-feed className="bg-surface/40" aria-label="For you">
      <div className="mx-auto max-w-7xl px-4 pt-6">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
          <Sparkles className="h-3.5 w-3.5" />
          {firstName ? `For you, ${firstName}` : 'For you'}
        </p>
      </div>
      <div className="divide-y divide-hairline">{children}</div>
      <p className="mx-auto max-w-7xl px-4 pb-6 text-xs text-ink-faint">
        Built from your saved searches and saved homes.{' '}
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 font-medium text-emerald-700 underline-offset-4 hover:text-emerald-600 hover:underline"
        >
          Your dashboard
          <ArrowRight className="h-3 w-3" />
        </Link>
      </p>
    </section>
  );
}
